import React, { useState } from "react";
import { Link } from "react-router-dom";
import { getConsultationPath } from "../../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../../constants/webLeadModules";
import {
  ArrowLeft,
  Calculator,
  RotateCcw,
  TrendingUp,
  IndianRupee,
  Clock,
  Percent,
  BarChart2,
  Layers,
  GitMerge,
  Scale,
} from "lucide-react";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import Swal from "sweetalert2";
import "./MutualFundCalculator.css";

const CALC_TYPES = [
  {
    value: "sip",
    label: "SIP Calculator",
    desc: "Systematic Investment Plan returns",
    color: "#2563eb",
    colorEnd: "#60a5fa",
    Icon: TrendingUp,
  },
  {
    value: "lumpsum",
    label: "Lumpsum Calculator",
    desc: "One-time investment growth",
    color: "#0891b2",
    colorEnd: "#38bdf8",
    Icon: IndianRupee,
  },
  {
    value: "stepsip",
    label: "Step-Up SIP",
    desc: "Fixed annual increment SIP",
    color: "#7c3aed",
    colorEnd: "#a78bfa",
    Icon: Layers,
  },
  {
    value: "compare",
    label: "SIP vs Lumpsum",
    desc: "Side-by-side comparison",
    color: "#10b981",
    colorEnd: "#6ee7b7",
    Icon: Scale,
  },
];

const formatINR = (n) =>
  Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

/* ── Year-by-year breakdown generators ── */
const buildSIPBreakdown = (sipAmount, annualRate, totalYears) => {
  const r = annualRate / 100 / 12;
  const rows = [];
  let cumulativeInvested = 0;

  for (let y = 1; y <= totalYears; y++) {
    const n = y * 12;
    const corpus = sipAmount * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
    cumulativeInvested = sipAmount * n;
    const returns = corpus - cumulativeInvested;
    rows.push({
      year: y,
      invested: Math.round(cumulativeInvested),
      returns: Math.round(returns),
      corpus: Math.round(corpus),
    });
  }
  return rows;
};

const buildLumpsumBreakdown = (principal, annualRate, totalYears) => {
  const r = annualRate / 100;
  const rows = [];
  for (let y = 1; y <= totalYears; y++) {
    const corpus = principal * Math.pow(1 + r, y);
    const returns = corpus - principal;
    rows.push({
      year: y,
      invested: Math.round(principal),
      returns: Math.round(returns),
      corpus: Math.round(corpus),
    });
  }
  return rows;
};

const buildStepUpBreakdown = (initialSIP, annualRate, totalYears, stepUp) => {
  const r = annualRate / 100 / 12;
  const rows = [];
  let corpus = 0;
  let cumulativeInvested = 0;
  let current = initialSIP;

  for (let y = 1; y <= totalYears; y++) {
    let yearInvested = 0;
    for (let m = 0; m < 12; m++) {
      corpus = corpus * (1 + r) + current;
      yearInvested += current;
      cumulativeInvested += current;
    }
    const returns = corpus - cumulativeInvested;
    rows.push({
      year: y,
      monthlySIP: Math.round(current),
      invested: Math.round(cumulativeInvested),
      returns: Math.round(returns),
      corpus: Math.round(corpus),
    });
    current += stepUp;
  }
  return rows;
};

const buildCompareBreakdown = (amount, annualRate, totalYears) => {
  const r = annualRate / 100 / 12;
  const rA = annualRate / 100;
  const rows = [];

  for (let y = 1; y <= totalYears; y++) {
    const n = y * 12;
    const sipCorpus = amount * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
    const sipInvested = amount * n;
    const lsCorpus = amount * Math.pow(1 + rA, y);

    rows.push({
      year: y,
      sipInvested: Math.round(sipInvested),
      sipCorpus: Math.round(sipCorpus),
      sipReturns: Math.round(sipCorpus - sipInvested),
      lsInvested: Math.round(amount),
      lsCorpus: Math.round(lsCorpus),
      lsReturns: Math.round(lsCorpus - amount),
    });
  }
  return rows;
};

export default function MutualFundCalculator() {
  const [calcType, setCalcType] = useState("sip");
  const [amount, setAmount] = useState("");
  const [rate, setRate] = useState("");
  const [years, setYears] = useState("");
  const [stepUp, setStepUp] = useState("");
  const [result, setResult] = useState(null);

  const active = CALC_TYPES.find((t) => t.value === calcType);
  const accentColor = active?.color || "#2563eb";
  const accentEnd = active?.colorEnd || "#60a5fa";

  /* ── Calculations ── */
  const calcSIP = () => {
    const SIP = Number(amount);
    const r = Number(rate) / 100 / 12;
    const n = Number(years) * 12;
    return SIP * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
  };

  const calcLumpsum = () => {
    const P = Number(amount);
    const r = Number(rate) / 100;
    const t = Number(years);
    return P * Math.pow(1 + r, t);
  };

  const calcStepUpTotal = () => {
    let FV = 0;
    const SIP = Number(amount);
    const r = Number(rate) / 100 / 12;
    const step = Number(stepUp);
    const months = Number(years) * 12;
    let current = SIP;
    for (let i = 1; i <= months; i++) {
      FV = FV * (1 + r) + current;
      if (i % 12 === 0) current += step;
    }
    return FV;
  };

  const handleCalculate = () => {
    if (!amount || !rate || !years) {
      Swal.fire({
        title: "Missing Details",
        text: "Please fill in all fields to calculate.",
        icon: "warning",
        confirmButtonText: "Got it",
        confirmButtonColor: accentColor,
        width: 380,
      });
      return;
    }
    if (calcType === "stepsip" && !stepUp) {
      Swal.fire({
        title: "Missing Step-Up Amount",
        text: "Please enter the annual step-up amount.",
        icon: "warning",
        confirmButtonText: "Got it",
        confirmButtonColor: accentColor,
        width: 380,
      });
      return;
    }

    const A = Number(amount);
    const R = Number(rate);
    const Y = Number(years);
    const S = Number(stepUp);

    switch (calcType) {
      case "sip":
        setResult({
          type: "single",
          fv: calcSIP(),
          breakdown: buildSIPBreakdown(A, R, Y),
        });
        break;
      case "lumpsum":
        setResult({
          type: "single",
          fv: calcLumpsum(),
          breakdown: buildLumpsumBreakdown(A, R, Y),
        });
        break;
      case "stepsip":
        setResult({
          type: "stepsip",
          fv: calcStepUpTotal(),
          breakdown: buildStepUpBreakdown(A, R, Y, S),
        });
        break;
      case "compare":
        setResult({
          type: "compare",
          sip: calcSIP(),
          lumpsum: calcLumpsum(),
          breakdown: buildCompareBreakdown(A, R, Y),
        });
        break;
      default:
        break;
    }
  };

  const handleReset = () => {
    setAmount("");
    setRate("");
    setYears("");
    setStepUp("");
    setResult(null);
  };

  const totalInvested =
    calcType === "lumpsum"
      ? Number(amount)
      : Number(amount) * Number(years) * 12;

  const fvForSummary =
    result?.type === "single" || result?.type === "stepsip"
      ? Math.round(result.fv)
      : 0;

  const wealthGained =
    result?.type === "single" || result?.type === "stepsip"
      ? fvForSummary - totalInvested
      : null;

  const investedPct =
    fvForSummary > 0 ? Math.round((totalInvested / fvForSummary) * 100) : 0;

  /* ── Table columns by type ── */
  const isSingle = result?.type === "single";
  const isStepUp = result?.type === "stepsip";
  const isCompare = result?.type === "compare";

  return (
    <div className="mf-wrapper">
      <Navbar />

      {/* ── Hero ── */}
      <section className="mf-hero">
        <div className="mf-hero-bg" />
        <div className="mf-hero-dots" />
        <div className="mf-hero-glow mf-glow-l" />
        <div className="mf-hero-glow mf-glow-r" />

        <div className="mf-hero-inner">
          <Link to="/calculators" className="mf-back-btn">
            <ArrowLeft size={14} /> Back to Calculators
          </Link>

          <div className="mf-hero-top">
            <div>
              <div className="mf-eyebrow">
                <BarChart2 size={12} /> Mutual Fund Calculators
              </div>
              <h1 className="mf-hero-title">
                Grow Your Wealth
                <br />
                <span
                  className="mf-gradient-text"
                  style={{
                    backgroundImage: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                  }}
                >
                  with Smart Investing
                </span>
              </h1>
              <p className="mf-hero-sub">
                Estimate SIP returns, lump-sum growth, step-up SIP and compare
                strategies — all in one place.
              </p>
            </div>

            <div
              className="mf-hero-icon-box"
              style={{
                background: `linear-gradient(135deg, ${accentColor}, ${accentEnd})`,
              }}
            >
              {active ? (
                <active.Icon size={36} color="#fff" />
              ) : (
                <BarChart2 size={36} color="#fff" />
              )}
            </div>
          </div>

          {/* Calculator type chips */}
          <div className="mf-type-chips">
            {CALC_TYPES.map(({ value, label, color, Icon }) => (
              <button
                key={value}
                className={`mf-chip${calcType === value ? " mf-chip--active" : ""}`}
                style={
                  calcType === value
                    ? { borderColor: color, background: `${color}14`, color }
                    : {}
                }
                onClick={() => {
                  setCalcType(value);
                  setResult(null);
                }}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Main ── */}
      <div className="mf-content">
        {/* Input Card */}
        <div className="mf-card">
          <div
            className="mf-card-bar"
            style={{
              background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
            }}
          />
          <div className="mf-card-body">
            <div
              className="mf-active-pill"
              style={{
                background: `${accentColor}10`,
                borderColor: `${accentColor}22`,
                color: accentColor,
              }}
            >
              {active && <active.Icon size={13} />}
              {active?.label} — {active?.desc}
            </div>

            <div className="mf-section-title">
              Investment Details
              <span className="mf-section-line" />
            </div>

            <div className="mf-grid-2">
              {/* Amount */}
              <div className="mf-field">
                <label className="mf-label">
                  <IndianRupee size={12} />
                  {calcType === "lumpsum"
                    ? "Investment Amount (₹)"
                    : "Monthly SIP Amount (₹)"}
                </label>
                <div className="mf-input-wrap">
                  <span className="mf-input-icon">
                    <IndianRupee size={14} />
                  </span>
                  <input
                    type="number"
                    className="mf-input"
                    placeholder={
                      calcType === "lumpsum" ? "e.g., 100000" : "e.g., 5000"
                    }
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
              </div>

              {/* Rate */}
              <div className="mf-field">
                <label className="mf-label">
                  <Percent size={12} /> Expected Annual Return (%)
                </label>
                <div className="mf-input-wrap">
                  <span className="mf-input-icon">
                    <Percent size={14} />
                  </span>
                  <input
                    type="number"
                    className="mf-input"
                    placeholder="e.g., 12"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                </div>
              </div>

              {/* Years */}
              <div className="mf-field">
                <label className="mf-label">
                  <Clock size={12} /> Investment Duration (Years)
                </label>
                <div className="mf-input-wrap">
                  <span className="mf-input-icon">
                    <Clock size={14} />
                  </span>
                  <input
                    type="number"
                    className="mf-input"
                    placeholder="e.g., 10"
                    value={years}
                    onChange={(e) => setYears(e.target.value)}
                  />
                </div>
              </div>

              {/* Step-Up */}
              {calcType === "stepsip" && (
                <div className="mf-field">
                  <label className="mf-label">
                    <TrendingUp size={12} /> Annual Step-Up Amount (₹)
                  </label>
                  <div className="mf-input-wrap">
                    <span className="mf-input-icon">
                      <IndianRupee size={14} />
                    </span>
                    <input
                      type="number"
                      className="mf-input"
                      placeholder="e.g., 1000"
                      value={stepUp}
                      onChange={(e) => setStepUp(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="mf-btn-row">
              <button
                className="mf-calc-btn"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
                onClick={handleCalculate}
              >
                <Calculator size={16} /> Calculate
              </button>
              <button className="mf-reset-btn" onClick={handleReset}>
                <RotateCcw size={15} /> Reset
              </button>
            </div>
          </div>
        </div>

        {/* ── Results ── */}
        {result && (
          <>
            {/* ── Single / Step-Up Summary ── */}
            {(isSingle || isStepUp) && (
              <div className="mf-card">
                <div
                  className="mf-card-bar"
                  style={{
                    background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                  }}
                />
                <div className="mf-card-body">
                  <div className="mf-section-title">
                    Results
                    <span className="mf-section-line" />
                  </div>

                  <div className="mf-result-grid">
                    {[
                      {
                        label: "Future Value",
                        value: fvForSummary,
                        color: accentColor,
                      },
                      {
                        label: "Total Invested",
                        value: totalInvested,
                        color: "#0891b2",
                      },
                      {
                        label: "Wealth Gained",
                        value: wealthGained,
                        color: "#10b981",
                      },
                    ].map(({ label, value, color }) => (
                      <div
                        key={label}
                        className="mf-result-card"
                        style={{
                          background: `linear-gradient(135deg, ${color}08, ${color}16)`,
                          borderColor: `${color}28`,
                        }}
                      >
                        <div className="mf-result-label">{label}</div>
                        <div className="mf-result-value" style={{ color }}>
                          ₹ {formatINR(value)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mf-section-title mf-section-title--spaced">
                    Portfolio Breakdown
                    <span className="mf-section-line" />
                  </div>

                  {[
                    {
                      label: "Amount Invested",
                      pct: investedPct,
                      amt: totalInvested,
                      color: "#0891b2",
                    },
                    {
                      label: "Wealth Gained",
                      pct: 100 - investedPct,
                      amt: wealthGained,
                      color: accentColor,
                    },
                  ].map(({ label, pct, amt, color }) => (
                    <div key={label} className="mf-bar-group">
                      <div className="mf-bar-row">
                        <span className="mf-bar-label">
                          {label} ({pct}%)
                        </span>
                        <span className="mf-bar-amt">₹ {formatINR(amt)}</span>
                      </div>
                      <div className="mf-bar-track">
                        <div
                          className="mf-bar-fill"
                          style={{
                            width: `${pct}%`,
                            background: `linear-gradient(90deg, ${color}, ${color}88)`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Comparison Summary ── */}
            {isCompare && (
              <div className="mf-card">
                <div
                  className="mf-card-bar"
                  style={{
                    background: `linear-gradient(90deg, #2563eb, #10b981)`,
                  }}
                />
                <div className="mf-card-body">
                  <div className="mf-section-title">
                    SIP vs Lumpsum Comparison
                    <span className="mf-section-line" />
                  </div>

                  <div className="mf-compare-grid">
                    <div className="mf-compare-col">
                      <div
                        className="mf-compare-badge"
                        style={{
                          background: "#2563eb14",
                          color: "#2563eb",
                          borderColor: "#2563eb22",
                        }}
                      >
                        <TrendingUp size={13} /> SIP
                      </div>
                      <div
                        className="mf-compare-value"
                        style={{ color: "#2563eb" }}
                      >
                        ₹ {formatINR(Math.round(result.sip))}
                      </div>
                      <div className="mf-compare-sub">Future Value</div>
                      <div
                        className="mf-compare-gain"
                        style={{ color: "#10b981" }}
                      >
                        +₹ {formatINR(Math.round(result.sip - totalInvested))}{" "}
                        gained
                      </div>
                    </div>

                    <div className="mf-compare-vs">VS</div>

                    <div className="mf-compare-col">
                      <div
                        className="mf-compare-badge"
                        style={{
                          background: "#0891b214",
                          color: "#0891b2",
                          borderColor: "#0891b222",
                        }}
                      >
                        <IndianRupee size={13} /> Lumpsum
                      </div>
                      <div
                        className="mf-compare-value"
                        style={{ color: "#0891b2" }}
                      >
                        ₹ {formatINR(Math.round(result.lumpsum))}
                      </div>
                      <div className="mf-compare-sub">Future Value</div>
                      <div
                        className="mf-compare-gain"
                        style={{ color: "#10b981" }}
                      >
                        +₹{" "}
                        {formatINR(Math.round(result.lumpsum - totalInvested))}{" "}
                        gained
                      </div>
                    </div>
                  </div>

                  <div
                    className="mf-diff-banner"
                    style={{
                      background:
                        result.sip > result.lumpsum ? "#2563eb0d" : "#0891b20d",
                      borderColor:
                        result.sip > result.lumpsum ? "#2563eb22" : "#0891b222",
                    }}
                  >
                    <GitMerge
                      size={16}
                      style={{
                        color:
                          result.sip > result.lumpsum ? "#2563eb" : "#0891b2",
                      }}
                    />
                    <span>
                      <strong>
                        {result.sip > result.lumpsum ? "SIP" : "Lumpsum"}
                      </strong>{" "}
                      outperforms by{" "}
                      <strong
                        style={{
                          color:
                            result.sip > result.lumpsum ? "#2563eb" : "#0891b2",
                        }}
                      >
                        ₹{" "}
                        {formatINR(
                          Math.abs(Math.round(result.sip - result.lumpsum)),
                        )}
                      </strong>
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* ── Yearly Growth Table — SIP ── */}
            {isSingle && calcType === "sip" && (
              <div className="mf-card">
                <div
                  className="mf-card-bar"
                  style={{
                    background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                  }}
                />
                <div className="mf-card-body">
                  <div className="mf-section-title">
                    Yearly SIP Growth Schedule
                    <span className="mf-section-line" />
                  </div>
                  <div className="mf-table-wrap">
                    <table className="mf-table">
                      <thead>
                        <tr>
                          {[
                            "Year",
                            "Invested (₹)",
                            "Returns (₹)",
                            "Total Corpus (₹)",
                          ].map((h) => (
                            <th key={h} className="mf-th">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {result.breakdown.map((row, i) => (
                          <tr
                            key={row.year}
                            className={i % 2 === 0 ? "mf-tr-even" : "mf-tr-odd"}
                          >
                            <td className="mf-td mf-td-year">
                              Year {row.year}
                            </td>
                            <td className="mf-td mf-td-mono">
                              {formatINR(row.invested)}
                            </td>
                            <td className="mf-td mf-td-mono mf-td-gain">
                              +{formatINR(row.returns)}
                            </td>
                            <td className="mf-td mf-td-mono mf-td-corpus">
                              {formatINR(row.corpus)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── Yearly Growth Table — Lumpsum ── */}
            {isSingle && calcType === "lumpsum" && (
              <div className="mf-card">
                <div
                  className="mf-card-bar"
                  style={{
                    background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                  }}
                />
                <div className="mf-card-body">
                  <div className="mf-section-title">
                    Yearly Lumpsum Growth Schedule
                    <span className="mf-section-line" />
                  </div>
                  <div className="mf-table-wrap">
                    <table className="mf-table">
                      <thead>
                        <tr>
                          {[
                            "Year",
                            "Principal (₹)",
                            "Returns Earned (₹)",
                            "Total Corpus (₹)",
                          ].map((h) => (
                            <th key={h} className="mf-th">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {result.breakdown.map((row, i) => (
                          <tr
                            key={row.year}
                            className={i % 2 === 0 ? "mf-tr-even" : "mf-tr-odd"}
                          >
                            <td className="mf-td mf-td-year">
                              Year {row.year}
                            </td>
                            <td className="mf-td mf-td-mono">
                              {formatINR(row.invested)}
                            </td>
                            <td className="mf-td mf-td-mono mf-td-gain">
                              +{formatINR(row.returns)}
                            </td>
                            <td className="mf-td mf-td-mono mf-td-corpus">
                              {formatINR(row.corpus)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── Yearly Growth Table — Step-Up SIP ── */}
            {isStepUp && (
              <div className="mf-card">
                <div
                  className="mf-card-bar"
                  style={{
                    background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                  }}
                />
                <div className="mf-card-body">
                  <div className="mf-section-title">
                    Yearly Step-Up SIP Growth Schedule
                    <span className="mf-section-line" />
                  </div>
                  <div className="mf-table-wrap">
                    <table className="mf-table">
                      <thead>
                        <tr>
                          {[
                            "Year",
                            "Monthly SIP (₹)",
                            "Invested (₹)",
                            "Returns (₹)",
                            "Total Corpus (₹)",
                          ].map((h) => (
                            <th key={h} className="mf-th">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {result.breakdown.map((row, i) => (
                          <tr
                            key={row.year}
                            className={i % 2 === 0 ? "mf-tr-even" : "mf-tr-odd"}
                          >
                            <td className="mf-td mf-td-year">
                              Year {row.year}
                            </td>
                            <td className="mf-td mf-td-mono mf-td-sip">
                              {formatINR(row.monthlySIP)}
                            </td>
                            <td className="mf-td mf-td-mono">
                              {formatINR(row.invested)}
                            </td>
                            <td className="mf-td mf-td-mono mf-td-gain">
                              +{formatINR(row.returns)}
                            </td>
                            <td className="mf-td mf-td-mono mf-td-corpus">
                              {formatINR(row.corpus)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── Yearly Comparison Table — SIP vs Lumpsum ── */}
            {isCompare && (
              <div className="mf-card">
                <div
                  className="mf-card-bar"
                  style={{
                    background: `linear-gradient(90deg, #2563eb, #10b981)`,
                  }}
                />
                <div className="mf-card-body">
                  <div className="mf-section-title">
                    Year-by-Year SIP vs Lumpsum Schedule
                    <span className="mf-section-line" />
                  </div>
                  <div className="mf-table-wrap">
                    <table className="mf-table mf-table--compare">
                      <thead>
                        <tr>
                          <th className="mf-th" rowSpan={2}>
                            Year
                          </th>
                          <th
                            className="mf-th mf-th-group"
                            colSpan={3}
                            style={{
                              color: "#2563eb",
                              borderBottom: "2px solid #2563eb22",
                            }}
                          >
                            SIP
                          </th>
                          <th
                            className="mf-th mf-th-group"
                            colSpan={3}
                            style={{
                              color: "#0891b2",
                              borderBottom: "2px solid #0891b222",
                            }}
                          >
                            Lumpsum
                          </th>
                        </tr>
                        <tr>
                          <th className="mf-th">Invested (₹)</th>
                          <th className="mf-th">Returns (₹)</th>
                          <th className="mf-th">Corpus (₹)</th>
                          <th className="mf-th">Invested (₹)</th>
                          <th className="mf-th">Returns (₹)</th>
                          <th className="mf-th">Corpus (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.breakdown.map((row, i) => {
                          const sipWins = row.sipCorpus >= row.lsCorpus;
                          return (
                            <tr
                              key={row.year}
                              className={
                                i % 2 === 0 ? "mf-tr-even" : "mf-tr-odd"
                              }
                            >
                              <td className="mf-td mf-td-year">
                                Year {row.year}
                              </td>
                              <td className="mf-td mf-td-mono">
                                {formatINR(row.sipInvested)}
                              </td>
                              <td className="mf-td mf-td-mono mf-td-gain">
                                +{formatINR(row.sipReturns)}
                              </td>
                              <td
                                className={`mf-td mf-td-mono ${sipWins ? "mf-td-winner" : "mf-td-corpus"}`}
                              >
                                {formatINR(row.sipCorpus)}
                                {sipWins && (
                                  <span className="mf-win-badge">↑</span>
                                )}
                              </td>
                              <td className="mf-td mf-td-mono">
                                {formatINR(row.lsInvested)}
                              </td>
                              <td className="mf-td mf-td-mono mf-td-gain">
                                +{formatINR(row.lsReturns)}
                              </td>
                              <td
                                className={`mf-td mf-td-mono ${!sipWins ? "mf-td-winner" : "mf-td-corpus"}`}
                              >
                                {formatINR(row.lsCorpus)}
                                {!sipWins && (
                                  <span className="mf-win-badge">↑</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* CTA Banner */}
        <div className="mf-cta-banner">
          <div className="mf-cta-text">
            <h3 className="mf-cta-title">Want a personalised MF portfolio?</h3>
            <p className="mf-cta-sub">
              Our advisors can build a goal-based mutual fund plan tailored to
              your risk appetite.
            </p>
          </div>
          <Link to={getConsultationPath(WEB_LEAD_MODULES.CALC_MF)} className="mf-cta-btn">
            Book Free Consultation →
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
